import { ROUTES, type DeployMode, type FeatureContext } from '@xantory/features';

// Landing page — login, the sidebar logo and the unknown-route fallback all
// lead here. Historically a fixed per-mode constant (cloud → fleet overview,
// local → farm status); R-146 makes it permission-derived so a user lands on
// the surface their role actually works in, with no stored preference.
//
// Base fallback when nothing more specific matches: cloud is multi-site, so it
// lands on the all-sites fleet overview (R-085); local is single-site, so the
// farm-wide status overview (user call 2026-07-04).
export const FALLBACK_ROUTE: Record<DeployMode, string> = {
  cloud: ROUTES.overview,
  local: ROUTES.iotControl,
};

/**
 * Pure derivation of the landing route from (mode, permissions) — no stored
 * preference. Precedence, highest first:
 *   0. CLOUD           → HQ / Fleet Overview, unconditionally (user directive
 *      2026-07-29). The cloud tier no longer derives its landing from role.
 *   1. `maintenance` Write → the incident console (LOCAL).
 *   2. `assets` Write  → the asset register.
 *   3. READ-only roles → the best surface they can actually READ (incidents,
 *      else the asset register). Without this a read-only role fell through to
 *      the local base fallback, IoT Control — whose every request is gated on
 *      `mapper:read`, which `viewer` deliberately does NOT hold (D-061). It
 *      landed on a page that 403'd on load and, once the nav gate was tightened
 *      to match the data, on a route that is not enabled at all.
 *   4. else            → the per-mode base fallback.
 *
 * NOTE: the caller (Router) does NOT re-verify that the result is enabled — it
 * routes straight to it — so this function must only ever return a surface the
 * permissions given actually open.
 */
export function defaultRoute(mode: DeployMode, ctx: Pick<FeatureContext, 'can'>): string {
  // CLOUD ALWAYS LANDS ON HQ → FLEET OVERVIEW (user directive 2026-07-29,
  // superseding R-146's soc-first precedence *for the cloud tier only*). The
  // cloud console is the all-sites view: an operator signing in expects to start
  // at the fleet, not already inside one of its dashboards — and `soc:read` is
  // broad enough that in practice every cloud user was being dropped on /soc.
  // Safe against this function's own contract (it must only return a surface the
  // permissions open): `overviewFeature` is gated on `mode === 'cloud'` alone,
  // with no permission check, so every cloud user can open it.
  // Local keeps the permission-derived ladder below unchanged.
  if (mode === 'cloud') return ROUTES.overview;
  // Everything below is LOCAL-only now — the cloud arms of these branches became
  // unreachable above, so they are gone rather than left as dead code.
  if (ctx.can('maintenance', 'write')) return ROUTES.maintenanceIncidents;
  if (ctx.can('assets', 'write')) return ROUTES.assets;
  // Read-only tiers, most useful first.
  if (ctx.can('maintenance', 'read')) return ROUTES.maintenanceIncidents;
  if (ctx.can('assets', 'read')) return ROUTES.assets;
  return FALLBACK_ROUTE[mode];
}
